import { useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import apiGet from 'lib/network/apiGet'
import moment from 'moment'
import { Field } from 'formik'
import Input from 'components/atoms/Input'
import { useTranslation } from 'react-i18next'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { arSA, enUS } from 'date-fns/locale'

registerLocale('ar', arSA)
registerLocale('en', enUS)

export const DashboardCards = ({ branchData }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const datePickerLocale = isArabic ? 'ar' : 'en'
  const [carStats, setCarStats] = useState({
    total: 0,
    active: 0,
    booked: 0,
    outOfService: 0,
    inProgress: 0,
    availableCars: 0,
    bookedCars: 0,
  })
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(null)

  useEffect(() => {
    fetchCarStats()
  }, [selectedDate, branchData])

  const fetchCarStats = async () => {
    try {
      setLoading(true)

      const params = {}

      if (branchData?._id) {
        params.branchId = branchData._id
      }

      if (selectedDate) {
        // Format using native Date methods to ensure English numerals
        const d = new Date(selectedDate)
        const year = d.getFullYear()
        const month = _.padStart(String(d.getMonth() + 1), 2, '0')
        const day = _.padStart(String(d.getDate()), 2, '0')
        params.dateParam = `${year}-${month}-${day}`
      }

      const response = await apiGet('stats', params)

      if (response?.success && response?.data?.rentalCars) {
        setCarStats(response.data.rentalCars)
      }
    } catch (error) {
      console.error('Error fetching car statistics:', error)
    } finally {
      setLoading(false)
    }
  }

  const DashboardCard = ({ title, value, subtitle }) => (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-200 mt-4">
      <div className="flex flex-col">
        <h3 className="text-xs font-medium text-gray-500">{title}</h3>
        <p className="mt-1 text-xl font-semibold text-gray-900">{loading ? '...' : _.isNumber(value) ? value.toLocaleString() : value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-600">{subtitle}</p>}
      </div>
    </div>
  )

  return (
    <>
      <div className="ml-4 mt-3 mb-4 w-full max-w-xs" dir={isArabic ? 'rtl' : 'ltr'}>
        <DatePicker
          selected={selectedDate}
          onChange={date => setSelectedDate(date)}
          dateFormat="dd/MM/yyyy"
          locale={datePickerLocale}
          placeholderText={isArabic ? 'اختر التاريخ' : 'dd/mm/yyyy'}
          className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
          isClearable
          popperPlacement="bottom-start"
          timeCaption={isArabic ? 'الوقت' : 'Time'}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <DashboardCard title={t('branchProfile.carStats.totalRentalCars')} value={carStats.total} />
        <DashboardCard title={t('branchProfile.carStats.activeCars')} value={carStats.active} />
        <DashboardCard title={t('branchProfile.carStats.bookedCars')} value={carStats.booked} />
        <DashboardCard title={t('branchProfile.carStats.outOfService')} value={carStats.outOfService} />
        <DashboardCard title={t('branchProfile.carStats.inProgress')} value={carStats.inProgress} />
        <DashboardCard title={t('branchProfile.carStats.availableCars')} value={carStats.availableCars} />
        <DashboardCard title={t('branchProfile.carStats.bookedCarsWithDate')} value={carStats.bookedCars} />
      </div>
    </>
  )
}
